import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Later } from "@/components/Later";
import { NotFound } from "@/features/shell/Problem";
import { StockCards } from "@/features/stock/StockCards";
import { StockTable, StockTableSkeleton } from "@/features/stock/StockTable";
import { ApiError } from "@/lib/api";
import { isoDay, formatTime } from "@/lib/format";
import {
  STOCK_VIEWS,
  countStock,
  filterStock,
  getStock,
  getStockRequest,
  groupStock,
  stockCsvHref,
  stockErrorCode,
  type StockRequest,
  type StockView,
} from "@/lib/stock";
import { PHONE, useMedia } from "@/lib/useMedia";

// Voorraad (H-v01 … H-v06, docs/partner-portal.md §16 step 7): WLX asks the warehouse when the customer looks; a
// moment later the stand comes, with the time it was taken. While it comes: a calm loading state, filters and Excel
// off (H-v02). When it does not come: no numbers, no older stand, Opnieuw proberen and a code (H-v03). Vernieuwen and
// Opnieuw proberen count down to a minute after the last question: WLX asks at most once a minute per company, and
// says so when many companies ask at once (busy, without a code). Filters per
// warning, owner (private labels, H-v04) and warehouse; per article the pallets fold open under it. Filters and search
// are in the address; the answer itself is not (it is five minutes old at most).

interface StockSearch {
  view?: Exclude<StockView, "all">;
  q?: string;
  owner?: string;
  warehouse?: string;
}

function text(value: unknown): string | undefined {
  if (typeof value === "number") return String(value);
  return typeof value === "string" && value.trim() ? value : undefined;
}

export const Route = createFileRoute("/_portal/stock")({
  validateSearch: (search: Record<string, unknown>): StockSearch => {
    const view = STOCK_VIEWS.find((v) => v !== "all" && v === search.view) as StockSearch["view"];
    return { view, q: text(search.q), owner: text(search.owner), warehouse: text(search.warehouse) };
  },
  component: StockScreen,
});

/** Ask again a second later while the question is under way; the server gives up after twenty seconds. */
const POLL_MS = 1000;

/** Seconds until Vernieuwen asks again (WLX asks at most once a minute per company); 0 when it may. */
function useSecondsUntil(at: string | undefined): number {
  const left = () => (at ? Math.max(0, Math.ceil((Date.parse(at) - Date.now()) / 1000)) : 0);
  const [seconds, setSeconds] = useState(left);
  useEffect(() => {
    setSeconds(left());
    if (!at) return;
    const handle = window.setInterval(() => {
      const next = left();
      setSeconds(next);
      if (next === 0) window.clearInterval(handle);
    }, 1000);
    return () => window.clearInterval(handle);
  }, [at]); // left() reads only `at`
  return seconds;
}

function StockScreen() {
  const { t, i18n } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const phone = useMedia(PHONE);
  const queryClient = useQueryClient();

  // Vernieuwen asks again: a new key, so the first question of this visit is the cached answer of the server.
  const [round, setRound] = useState(0);
  const start = useQuery({ queryKey: ["partner", "stock", round], queryFn: () => getStock(round > 0), staleTime: Infinity, retry: false });
  const id = start.data?.id;
  const poll = useQuery({
    queryKey: ["partner", "stock", "request", id],
    queryFn: () => getStockRequest(id!),
    enabled: start.data?.status === "pending",
    refetchInterval: (query) => (query.state.data?.status === "ok" || query.state.data?.status === "failed" ? false : POLL_MS),
    retry: false,
  });
  const request: StockRequest | undefined = start.data?.status === "pending" ? (poll.data ?? start.data) : start.data;
  const again = () => {
    void queryClient.removeQueries({ queryKey: ["partner", "stock", "request"] });
    setRound((n) => n + 1);
  };
  const wait = useSecondsUntil(request?.nextAt);

  const today = isoDay(0);
  const articles = useMemo(() => (request?.status === "ok" ? groupStock(request.lines ?? [], today) : []), [request, today]);
  const owners = useMemo(() => {
    const seen = new Map<string, string>();
    for (const article of articles) seen.set(article.owner.code, article.owner.name ?? article.owner.code);
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [articles]);
  const warehouses = useMemo(() => [...new Set(articles.flatMap((article) => article.warehouses))].sort(), [articles]);

  const view: StockView = search.view ?? "all";
  const scope = filterStock(articles, { view: "all", q: search.q, owner: search.owner, warehouse: search.warehouse }, today);
  const counts = countStock(scope);
  const shown = filterStock(scope, { view }, today);

  // Search as you type, in the address a moment after the last key.
  const [typed, setTyped] = useState(search.q ?? "");
  useEffect(() => {
    setTyped((current) => (current.trim() === (search.q ?? "") ? current : (search.q ?? "")));
  }, [search.q]);
  useEffect(() => {
    const q = typed.trim() || undefined;
    if (q === search.q) return;
    const handle = window.setTimeout(() => void navigate({ search: (prev) => ({ ...prev, q }), replace: true }), 300);
    return () => window.clearTimeout(handle);
  }, [typed, search.q, navigate]);

  if (start.error instanceof ApiError && start.error.status === 404) return <NotFound />;

  const ready = request?.status === "ok";
  const failed = request?.status === "failed" || (start.isError && !start.data);
  const place = warehouses.length === 1 ? t("stock.inWarehouse", { name: warehouses[0] }) : warehouses.length > 1 ? t("stock.inWarehouses", { count: warehouses.length }) : null;

  let content;
  if (failed) {
    // Busy: WLX did not ask, many questions came at once. Not a fault to quote, so no code.
    const busy = request?.error === "busy";
    content = (
      <div className="wl-empty" data-screen-label={busy ? "Portaal Voorraad druk" : "Portaal Voorraad mislukt"}>
        <div className="wl-empty__icon">
          <Icon name={busy ? "clock" : "alert"} size={20} />
        </div>
        <div className="wl-empty__title">{t(busy ? "stock.busyTitle" : "stock.failedTitle")}</div>
        <p className="wl-empty__text">{t(busy ? "stock.busyText" : "stock.failedText")}</p>
        <button type="button" className="wl-btn wl-btn--primary" onClick={again} disabled={wait > 0}>
          <Icon name="refresh" />
          {wait > 0 ? t("stock.retryIn", { seconds: wait }) : t("problem.retry")}
        </button>
        {request && !busy && <p className="wl-empty__text t-body-sm">{t("stock.failedCode", { code: stockErrorCode(request) })}</p>}
      </div>
    );
  } else if (!ready) {
    content = (
      <Later>
        <div className="wl-vstack" data-screen-label="Portaal Voorraad ophalen">
          <p className="t-body-sm" style={{ color: "var(--text-secondary)" }}>
            {t("stock.loading")}
          </p>
          {phone ? null : (
            <div className="wl-table-wrap">
              <StockTableSkeleton />
            </div>
          )}
        </div>
      </Later>
    );
  } else if (articles.length === 0) {
    content = (
      <div className="wl-empty" data-screen-label="Portaal Voorraad leeg">
        <div className="wl-empty__icon">
          <Icon name="building" size={20} />
        </div>
        <div className="wl-empty__title">{t("stock.emptyTitle")}</div>
        <p className="wl-empty__text">{t("stock.emptyText")}</p>
      </div>
    );
  } else if (shown.length === 0) {
    content = (
      <div className="wl-empty" data-screen-label="Portaal Voorraad niets gevonden">
        <div className="wl-empty__icon">
          <Icon name="search" size={20} />
        </div>
        <div className="wl-empty__title">{search.q ? t("stock.notFound", { q: search.q }) : t(`stock.none.${view}`)}</div>
        {(search.q || view !== "all" || search.owner || search.warehouse) && (
          <button
            type="button"
            className="wl-btn wl-btn--secondary"
            onClick={() => {
              setTyped("");
              void navigate({ search: {} });
            }}
          >
            {t("stock.showAll")}
          </button>
        )}
      </div>
    );
  } else if (phone) {
    content = <StockCards articles={shown} today={today} />;
  } else {
    content = (
      <div className="wl-table-wrap">
        <StockTable articles={shown} today={today} withOwner={owners.length > 1} />
      </div>
    );
  }

  return (
    <div className="wl-portal-split">
      <div className="wl-portal-page" data-screen-label="Portaal Voorraad">
        <div className="wl-portal-head">
          <div className="wl-vstack" style={{ gap: "var(--space-1)" }}>
            <h1 className="t-h2">{t("nav.stock")}</h1>
            <div className="wl-hstack t-body-sm" style={{ color: "var(--text-secondary)" }}>
              <Icon name={failed ? "info" : "clock"} size={14} />
              {ready && request?.asOf ? (
                <span>
                  {t("stock.asOf")} <span className="t-mono">{formatTime(request.asOf, i18n.language)}</span>
                  {place && ` · ${place}`}
                </span>
              ) : (
                <span>{failed ? t("stock.noStand") : t("stock.fetching")}</span>
              )}
              {(ready || failed) && (
                <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={again} disabled={wait > 0}>
                  <Icon name="refresh" />
                  {wait > 0 ? t("stock.refreshIn", { seconds: wait }) : t("stock.refresh")}
                </button>
              )}
            </div>
          </div>
          {!phone &&
            (ready && articles.length > 0 && request ? (
              <a className="wl-btn wl-btn--secondary" href={stockCsvHref(request.id)}>
                <Icon name="download" />
                {t("stock.excel")}
              </a>
            ) : (
              <button type="button" className="wl-btn wl-btn--secondary" disabled>
                <Icon name="download" />
                {t("stock.excel")}
              </button>
            ))}
        </div>

        <div className="wl-portal-toolbar">
          {!phone && (
            <div className="wl-segment" role="group" aria-label={t("stock.views")}>
              {STOCK_VIEWS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={v === view ? "wl-segment__btn is-active" : "wl-segment__btn"}
                  aria-pressed={v === view}
                  disabled={!ready}
                  onClick={() => void navigate({ search: (prev) => ({ ...prev, view: v === "all" ? undefined : v }) })}
                >
                  {t(`stock.view.${v}`)}
                  {ready && <span className="count">{counts[v]}</span>}
                </button>
              ))}
            </div>
          )}
          <label className="wl-input-group">
            <span className="wl-input-group__icon">
              <Icon name="search" />
            </span>
            <input
              type="search"
              value={typed}
              disabled={!ready}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={phone ? t("stock.searchShort") : t("stock.search")}
              aria-label={t("stock.search")}
            />
          </label>
        </div>

        {ready && (owners.length > 1 || warehouses.length > 1) && (
          <div className="wl-portal-toolbar">
            {owners.length > 1 && phone && (
              <label className="wl-hstack t-body-sm">
                <span style={{ color: "var(--text-secondary)" }}>{t("stock.owner")}</span>
                <select
                  className="wl-select"
                  value={search.owner ?? ""}
                  onChange={(event) => void navigate({ search: (prev) => ({ ...prev, owner: event.target.value || undefined }) })}
                >
                  <option value="">{t("stock.allOwners")}</option>
                  {owners.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {owners.length > 1 && !phone && (
              <div className="wl-hstack t-body-sm">
                <span style={{ color: "var(--text-secondary)" }}>{t("stock.owner")}</span>
                <div className="wl-segment" role="group" aria-label={t("stock.owner")}>
                  <button
                    type="button"
                    className={!search.owner ? "wl-segment__btn is-active" : "wl-segment__btn"}
                    aria-pressed={!search.owner}
                    onClick={() => void navigate({ search: (prev) => ({ ...prev, owner: undefined }) })}
                  >
                    {t("stock.allOwners")}
                  </button>
                  {owners.map(([code, name]) => (
                    <button
                      key={code}
                      type="button"
                      className={search.owner === code ? "wl-segment__btn is-active" : "wl-segment__btn"}
                      aria-pressed={search.owner === code}
                      onClick={() => void navigate({ search: (prev) => ({ ...prev, owner: code }) })}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {warehouses.length > 1 && (
              <label className="wl-hstack t-body-sm">
                <span style={{ color: "var(--text-secondary)" }}>{t("stock.warehouse")}</span>
                <select
                  className="wl-select"
                  value={search.warehouse ?? ""}
                  onChange={(event) => void navigate({ search: (prev) => ({ ...prev, warehouse: event.target.value || undefined }) })}
                >
                  <option value="">{t("stock.allWarehouses")}</option>
                  {warehouses.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        {content}
      </div>
    </div>
  );
}
